/**
 * What the board shows.
 *
 * ------------------------------------------------------------------
 * Two questions, two controls, and one rule that survives both:
 *
 *     the number on a control is the number of rows tapping it gives you.
 * ------------------------------------------------------------------
 *
 * **Waar** — on my road, or everywhere. A switch with two positions, and the
 * position is the answer to "why is this list short". Default: my road, because
 * that is the question a moving car has.
 *
 * **Wat** — one kind, or all kinds. A choice, not a stack: picking a type
 * replaces the last one rather than adding to it.
 *
 * How it got here is worth keeping. It started as three things that could all
 * narrow the same list at once — a service filter, an "op mijn weg" switch, and
 * the board's own `ahead` rule — each with its own count, none of which was the
 * length of the list. Five numbers on screen above two rows.
 *
 * The fix was to collapse them into one row of mutually exclusive scopes, which
 * made the counts honest and cost something real: "mobile cameras on my road"
 * stopped being expressible, and the row grew a chip per service until it ran
 * off the side of the screen. Reported, again: the filter should be clearer and
 * friendlier.
 *
 * So: two dimensions, but only one of them ever grows. `waar` is always exactly
 * two options and `wat` lives in a menu, so the row is a switch, at most one
 * type chip, and a button. It cannot outgrow the screen.
 *
 * The one piece of behaviour worth spelling out: **picking a type moves you to
 * "Alles"**. "If somebody wants to know where all the mobile cameras are, they
 * tap that filter" — so tapping it has to show all of them, not the two that
 * happen to be on this road. It is not a hidden override any more; the switch
 * visibly slides across, and sliding it back gives you that type on your own
 * road. Two taps for a combination, nothing silent.
 */

export const WHERE_ROAD = "road";
export const WHERE_ALL = "all";

/** Where the board starts: what is on your road, ahead of you, alles erop. */
export const DEFAULT_FILTER = { where: WHERE_ROAD, types: [] };

/**
 * Welke soorten er eerst in het menu staan.
 *
 * Niet op aantal. Dat stond er, en het gaf dit: 846 × rijstrook dicht, 642 ×
 * wegwerkzaamheden, 229 × pech — en "Mobiele flitser" als elfde regel
 * onderaan, met een 1 erachter. Van de weg gemeld als "ik zie mobiele flitser
 * niet in de filter staan", en dat klopte: hij stond er wel, maar achter
 * zeventienhonderd rijen NDW.
 *
 * De aantallen zeggen hoeveel er ligt, niet waar je voor komt. Een controle
 * zoek je op; werk in uitvoering kom je tegen. Dus eerst waar je naar zoekt.
 */
export const TONE_RANK = {
  flits: 0, pol: 0, kmar: 0, dou: 0, bel: 0, taxi: 0, ilt: 0,
  sit: 1,
  werk: 2,
};

/**
 * Whether one report is on the driver's own road.
 *
 * `ahead` is the board's own call — bound to another carriageway, or already
 * behind you. `on_route` is the stricter "not on this road at all". Both are
 * answered by the server for every report; which of them counts is decided
 * here and nowhere else.
 */
export const onMyRoad = (m) => m.ahead !== false && m.on_route !== false;

/** The city filter from the menu. Narrows the counts too, so the rule holds. */
export const inCities = (m, cities = []) =>
  cities.length === 0 || cities.includes(m.city);

/**
 * Wat een filter overlaat.
 *
 * "Alles" is alles, en dat is een correctie. Er stond hier een regel
 * (`in_overview`, van de server) die palen — een vaste flitser, een
 * trajectcontrole, een roodlichtcamera — uit "Alles" hield zodra ze niet op je
 * eigen weg lagen: er zijn er duizenden en ze zouden een lijst over je omgeving
 * verdringen.
 *
 * Van de weg gemeld: ze waren in geen van beide standen meer te zien.
 * Nagemeten op de A10 en het klopte precies — zonder koers zes palen, vier op
 * je weg en vier in Alles; met koers 270 nul en nul. De regel leunde op
 * dezelfde `ahead`-toets als "Op mijn weg", dus zodra je een kant op reed,
 * snoeiden beide standen hetzelfde weg. Twee knoppen die hetzelfde doen zijn
 * geen twee knoppen.
 *
 * De reden dat hij bestond is intussen anders opgelost: "Alles" groepeert per
 * type (zie groupByType), dus veertig palen zijn één kop met veertig regels
 * eronder en verdringen niets meer.
 */
export function matches(m, filter) {
  const f = { ...DEFAULT_FILTER, ...(filter || {}) };
  const gekozen = f.types || [];
  if (gekozen.length && !gekozen.includes(m.authority)) return false;
  if (f.where === WHERE_ROAD && !onMyRoad(m)) return false;
  return true;
}

/**
 * The rows, in filter order.
 *
 * City first so that everything downstream — the list and every count — is
 * talking about the same set of reports.
 */
export function visibleReports(items, filter, cities = []) {
  return items.filter(m => inCities(m, cities) && matches(m, filter));
}

/** What a control promises. Same function as the list, so they cannot drift. */
export function countFor(items, filter, cities = []) {
  return visibleReports(items, filter, cities).length;
}

/**
 * The types actually on the board, each with the number tapping it gives.
 *
 * Counted at `where: all`, because that is where tapping one lands you. A menu
 * row reading "Mobiele flitser 3" above a list of one would be the same broken
 * promise this file exists to prevent.
 */
export function typeChoices(items, cities = [], rank = null) {
  const tally = new Map();
  // Over dezelfde verzameling als "Alles", en niet over alles wat binnenkwam:
  // anders belooft het menu een aantal dat de lijst eronder niet levert.
  for (const m of visibleReports(items, { where: WHERE_ALL, type: null }, cities)) {
    if (!m.authority) continue;
    const seen = tally.get(m.authority);
    if (seen) seen.count += 1;
    else tally.set(m.authority, { id: m.authority, name: m.authority_name, count: 1 });
  }
  // Groep eerst, dan aantal, dan naam.
  //
  // De groep is nieuw en hij is de hele reden dat dit veranderd is: op aantal
  // alleen stond "Mobiele flitser" elfde, onder 846 rijstroken en 642
  // werkzaamheden. Zie TONE_RANK. `rank` komt van buiten omdat dit bestand
  // geen React en geen kleurtabel wil kennen; zonder meegegeven rank blijft
  // het precies wat het was.
  //
  // Het aantal daarna, en de naam als tiebreak. Die laatste is geen versiering:
  // met twee soorten op hetzelfde aantal zou de volgorde afhangen van welke
  // melding toevallig het eerst binnenkwam, en dan schuift het menu onder je
  // duim door bij elke poll.
  const groep = (c) => (rank ? (rank(c.id) ?? 99) : 0);
  return [...tally.values()].sort(
    (a, b) => groep(a) - groep(b) || b.count - a.count
      || a.name.localeCompare(b.name, "nl"),
  );
}

/**
 * "Alles" als overzicht: per type bij elkaar, in plaats van op afstand.
 *
 * De twee standen beantwoorden verschillende vragen en verdienen daarom een
 * andere ordening. "Op mijn weg" is wat er aankomt — dat is een rijlijst en die
 * hoort op afstand, dichtstbij eerst. "Alles" is wat er in de buurt staat, en
 * dan is afstand juist de minst nuttige as: vier mobiele flitsers verspreid
 * over de stad, om en om met drie wegwerkzaamheden, is geen overzicht maar een
 * opsomming.
 *
 * Krijgt de rijen die al gefilterd en gesorteerd zijn, zodat de volgorde binnen
 * een groep blijft wat de lezer koos. Leeg zodra er een type gekozen is of je
 * op je eigen weg kijkt — dan is er niets te groeperen en tekent het scherm
 * gewoon de rijen.
 */
export function groupByType(rows, filter) {
  const f = { ...DEFAULT_FILTER, ...(filter || {}) };
  // Eén type gekozen: dan is er één groep en is een kop erboven een lege
  // herhaling. Twee of meer: dan doet de groepering juist weer werk.
  if (f.where !== WHERE_ALL || (f.types || []).length === 1) return [];
  const groups = new Map();
  for (const m of rows) {
    if (!m.authority) continue;
    const seen = groups.get(m.authority);
    if (seen) seen.rows.push(m);
    else groups.set(m.authority, {
      id: m.authority, name: m.authority_name || m.authority, rows: [m],
    });
  }
  // Zelfde ordening als het typemenu: aantal, dan naam. Op aantal alleen
  // herschikt de lijst zich onder je duim bij elke verversing.
  return [...groups.values()].sort(
    (a, b) => b.rows.length - a.rows.length || a.name.localeCompare(b.name, "nl"),
  );
}

/**
 * Wat de kaart tekent bij een gekozen type. Null is alles.
 *
 * Hier en niet in het scherm, om precies de reden die de Android-kaart al had:
 * "de filter werkt niet" is een melding die je alleen kunt weerleggen als de
 * keuze buiten het scherm te draaien is. Daar zat de fout in de tekening en niet
 * in de keuze, en zonder een losse functie was dat niet vast te stellen.
 *
 * Dezelfde woordenschat als het bord — [typeChoices] levert de knoppen — zodat
 * de kaart en de lijst hetzelfde bedoelen met "type". Op Android doet MapFilter
 * hetzelfde met dezelfde ordening.
 */
export const pinsOfTypes = (pins, types) =>
  (types || []).length
    ? (pins || []).filter(p => (types || []).includes(p.authority))
    : (pins || []);

/**
 * Een soort aan- of uitzetten.
 *
 * Wat hier stond en weg is: één type kiezen zette de schakelaar ook op
 * "Omgeving". De reden was goed — "waar staan alle mobiele flitsers" is een
 * vraag over de stad en niet over jouw weg — maar hij hoorde bij een keuze
 * die er één was. Nu is dit een ruisfilter: je vinkt wegwerkzaamheden uit
 * omdat je er drie schermen vol van hebt. Dan is meeverspringen naar
 * "Omgeving" geen behulpzaamheid maar het verliezen van je plek, elke keer
 * dat je één vinkje verzet.
 *
 * De schakelaar beslist voortaan alleen zelf waar je kijkt.
 */
export const toggleType = (filter, type) => {
  const nu = filter.types || [];
  return {
    ...filter,
    types: nu.includes(type) ? nu.filter(t => t !== type) : [...nu, type],
  };
};

/** Alles weer aan. */
export const allTypes = (filter) => ({ ...filter, types: [] });

export const isDefault = (filter) =>
  filter.where === DEFAULT_FILTER.where && !(filter.types || []).length;
